import { useCallback, useRef } from "react";

/**
 * Touch/mouse long-press detection that never swallows a normal tap.
 * The consumer receives handlers to spread on the target element; a tap
 * still fires the element's own onClick, a long press calls `onLongPress`
 * and suppresses the following click.
 */
export function useLongPress(onLongPress: () => void, delay = 500) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const begin = useCallback(
    (x: number, y: number) => {
      fired.current = false;
      start.current = { x, y };
      clear();
      timer.current = setTimeout(() => {
        fired.current = true;
        onLongPress();
      }, delay);
    },
    [clear, delay, onLongPress],
  );

  const move = useCallback(
    (x: number, y: number) => {
      if (!start.current) return;
      if (Math.abs(x - start.current.x) > 10 || Math.abs(y - start.current.y) > 10) clear();
    },
    [clear],
  );

  return {
    handlers: {
      onTouchStart: (e: React.TouchEvent) => {
        const t = e.touches[0];
        if (t) begin(t.clientX, t.clientY);
      },
      onTouchMove: (e: React.TouchEvent) => {
        const t = e.touches[0];
        if (t) move(t.clientX, t.clientY);
      },
      onTouchEnd: clear,
      onTouchCancel: clear,
      onMouseDown: (e: React.MouseEvent) => begin(e.clientX, e.clientY),
      onMouseMove: (e: React.MouseEvent) => move(e.clientX, e.clientY),
      onMouseUp: clear,
      onMouseLeave: clear,
      onContextMenu: (e: React.MouseEvent) => {
        // Long-press on touch often raises a context menu; keep the gesture clean.
        if (fired.current) e.preventDefault();
      },
      onClick: (e: React.MouseEvent) => {
        if (fired.current) {
          e.preventDefault();
          e.stopPropagation();
          fired.current = false;
        }
      },
    },
    didLongPress: () => fired.current,
    cancel: clear,
  };
}
